import { useContext, useMemo } from 'react'
import { Pressable, Text, View } from 'react-native'
import { Slot, useGlobalSearchParams, useRouter, useSegments } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { SegmentedControl } from '@/components/base'
import CustomerServiceButton from '@/components/CustomerServiceButton'
import { Stack, TabScreenContent } from '@/components/layout'
import NotificationButton from '@/components/NotificationButton'
import { ActionableOrderNotificationsContext } from '@/components/providers/notifications'
import ScreenHeader from '@/components/ScreenHeader'

type OrderListAction = 'buy' | 'sell' | 'fast-track'

const screenOptions = { header: () => <Header /> }

export default function OrderLayout() {
  const router = useRouter()
  const { t } = useTranslation()
  const rawAction = useGlobalSearchParams().action
  const action: OrderListAction = rawAction === 'sell' || rawAction === 'fast-track' ? rawAction : 'buy'
  const status = useSegments().at(-1) as 'open' | 'closed'

  const tabs = [
    { status: 'open' as const, label: t('nav.orderTab.open') },
    { status: 'closed' as const, label: t('nav.orderTab.closed') }
  ]

  return (
    <>
      <Stack.Screen options={screenOptions} />

      <TabScreenContent>
        <View className={'flex-1'}>
          <View className={'h-11 flex-row border-b border-border-10 bg-background-primary'}>
            {tabs.map((tab) => {
              const selected = status === tab.status
              return (
                <Pressable
                  accessibilityRole={'tab'}
                  accessibilityState={{ selected }}
                  className={'h-full flex-1 items-center justify-center'}
                  key={tab.status}
                  onPress={() => router.replace(`/order-list/${action}/${tab.status}`)}
                >
                  <Text
                    className={
                      selected ? 'text-sm font-medium text-text-primary' : 'text-sm font-medium text-text-tertiary'
                    }
                  >
                    {tab.label}
                  </Text>
                  {selected && <View className={'absolute bottom-[-1px] h-0.5 w-20 rounded-t bg-primary'} />}
                </Pressable>
              )
            })}
          </View>
          <View className={'flex-1'}>
            <Slot />
          </View>
        </View>
      </TabScreenContent>
    </>
  )
}

function Header() {
  const { numActionableBuyOrders, numActionableSellOrders } = useContext(ActionableOrderNotificationsContext)
  const { t } = useTranslation()
  const { top } = useSafeAreaInsets()
  const router = useRouter()
  const rawAction = useGlobalSearchParams().action
  const action: OrderListAction = rawAction === 'sell' || rawAction === 'fast-track' ? rawAction : 'buy'
  const status = useSegments().at(-1) as 'open' | 'closed'

  const options = useMemo(() => {
    return [
      { label: t('nav.orderTab.buy'), value: 'buy', hasNew: numActionableBuyOrders > 0 },
      { label: t('nav.orderTab.sell'), value: 'sell', hasNew: numActionableSellOrders > 0 },
      { label: t('nav.orderTab.fastTrack'), value: 'fast-track', hasNew: false }
    ]
  }, [t, numActionableBuyOrders, numActionableSellOrders])

  return (
    <View className={'bg-background-primary'} style={{ paddingTop: top }}>
      <ScreenHeader
        leftElement={<CustomerServiceButton />}
        centerElement={
          <SegmentedControl
            className={'w-[216px]'}
            options={options}
            value={action}
            onChange={(action) => router.push(`/order-list/${action}/${status}`)}
          />
        }
        rightElement={<NotificationButton />}
      />
    </View>
  )
}
